/**
 * useCommerce — коммерция каталога клиентского приложения (featured-блок,
 * спецпредложения, персональное предложение). Только OO (perm actor_approval).
 * Хранится в app_config, читается/пишется через /api/commerce; источник
 * правды — бэкенд getCommerce.
 *
 * Backend:
 *  - GET /api/commerce            → CommerceConfig
 *  - PUT /api/commerce  body CommerceConfig → { ok, commerce: CommerceConfig }
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";

export interface CommerceFeatured {
  mode: "quest" | "custom";
  questId?: string;
  title?: string;
  subtitle?: string;
  imageUrl?: string;
  trailerUrl?: string;
}
export interface CommerceOffer {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
}
export interface CommercePersonalOffer {
  title: string;
  description: string;
  imageUrl?: string;
}
export interface CommerceConfig {
  featured: CommerceFeatured;
  offers: CommerceOffer[];
  personalOffer: CommercePersonalOffer | null;
}

export interface UseCommerceResult {
  config: CommerceConfig | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  save: (next: CommerceConfig) => Promise<{ ok: boolean; error?: string; config?: CommerceConfig }>;
}

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : "Ошибка";
}

export function useCommerce(): UseCommerceResult {
  const [config, setConfig] = useState<CommerceConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  useEffect(() => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await api<CommerceConfig>("/api/commerce", { signal: ac.signal });
        if (ac.signal.aborted) return;
        setConfig(r);
        setLoading(false);
      } catch (e: unknown) {
        if (ac.signal.aborted) return;
        setError(errMsg(e));
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [trigger]);

  const save = useCallback(async (next: CommerceConfig) => {
    try {
      const r = await api<{ ok: boolean; commerce: CommerceConfig }>("/api/commerce", { method: "PUT", body: next });
      if (r?.commerce) setConfig(r.commerce);
      return { ok: true, config: r?.commerce };
    } catch (e: unknown) {
      return { ok: false, error: errMsg(e) };
    }
  }, []);

  return { config, loading, error, reload, save };
}
